import { useRef, useState } from "react";
import { X, Sparkles, Upload, Link2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

// Three-tab add flow: Quick (default) / Paste & Parse / Full manual.
// The full form isn't strictly needed for MVP — quick + parse cover 90% of use.
// Full is just the same fields as quick with more optional lines revealed.

const REACT_KEYS = ["quick", "parse"];

export default function PropertyQuickAdd({ existing, onClose, onSaved }) {
  const [mode, setMode] = useState("quick");
  const isEdit = !!existing;

  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center px-4 py-8 overflow-y-auto" role="dialog" aria-label={isEdit ? "Edit property" : "Add property"} data-testid="property-add-modal">
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg rounded-xl bg-white shadow-2xl">
        <div className="flex items-center justify-between px-6 pt-6 pb-3">
          <div>
            <h2 className="font-heading text-xl font-bold tracking-tight text-slate-900">{isEdit ? "Edit property" : "Add a property"}</h2>
            <p className="mt-1 text-xs text-slate-500">{isEdit ? "Update any field — existing shares will show the new details." : "Address + price is enough to start sharing. Everything else is optional."}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-md text-slate-400 hover:bg-slate-100" aria-label="Close" data-testid="property-add-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        {!isEdit && (
          <div className="px-6 border-b border-slate-100">
            <div className="flex gap-1">
              {REACT_KEYS.map(k => (
                <button
                  key={k}
                  onClick={() => setMode(k)}
                  className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${mode === k ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"}`}
                  data-testid={`property-mode-${k}`}
                >
                  {k === "quick" ? "Quick add" : "Paste & parse"}
                </button>
              ))}
            </div>
          </div>
        )}

        {isEdit || mode === "quick" ? <QuickForm existing={existing} onSaved={onSaved} /> : <ParseForm onSaved={onSaved} />}
      </div>
    </div>
  );
}

function QuickForm({ existing, onSaved }) {
  const { agent } = useAuth();
  const [f, setF] = useState({
    address_line1: existing?.address_line1 || "",
    city: existing?.city || "",
    // Default to the agent's own state (set on Profile) rather than
    // hard-coding "AZ" — supports the national roll-out.
    state: existing?.state || agent?.state || "",
    list_price: existing?.list_price ?? "",
    beds: existing?.beds ?? "",
    baths: existing?.baths ?? "",
    sqft: existing?.sqft ?? "",
    listing_url: existing?.listing_url || "",
    hero_image_url: existing?.hero_image_url || "",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const pickPhoto = () => fileRef.current?.click();

  const uploadPhoto = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) return toast.error("Photo must be under 8 MB");
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await api.post("/properties/photo", form, { headers: { "Content-Type": "multipart/form-data" } });
      setF(v => ({ ...v, hero_image_url: r.data.url }));
      toast.success("Photo uploaded");
    } catch {
      toast.error("Upload failed");
    } finally { setUploading(false); }
  };

  const save = async () => {
    if (!f.address_line1.trim()) return toast.error("Address is required");
    setSaving(true);
    try {
      const body = {
        address_line1: f.address_line1.trim(),
        city: f.city || null, state: f.state || null,
        list_price: f.list_price ? Number(f.list_price) : null,
        beds: f.beds ? Number(f.beds) : null,
        baths: f.baths ? Number(f.baths) : null,
        sqft: f.sqft ? Number(f.sqft) : null,
        listing_url: f.listing_url || null,
        hero_image_url: f.hero_image_url || null,
        entry_method: existing?.entry_method || "quick",
      };
      if (existing) {
        await api.patch(`/properties/${existing.id}`, body);
        toast.success("Property updated");
      } else {
        await api.post("/properties", body);
        toast.success("Property added");
      }
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="p-6 space-y-4">
      <Field label="Address" required>
        <input value={f.address_line1} onChange={e => setF({ ...f, address_line1: e.target.value })} placeholder="1420 E Ranch Rd" className="input" data-testid="qa-address" />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="City"><input value={f.city} onChange={e => setF({ ...f, city: e.target.value })} placeholder="Gilbert" className="input" data-testid="qa-city" /></Field>
        <Field label="State"><input value={f.state} onChange={e => setF({ ...f, state: e.target.value.toUpperCase().slice(0,2) })} maxLength={2} className="input uppercase" data-testid="qa-state" /></Field>
      </div>
      <Field label="List price">
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
          <input type="number" value={f.list_price} onChange={e => setF({ ...f, list_price: e.target.value })} placeholder="625000" className="input pl-6" data-testid="qa-price" />
        </div>
      </Field>
      <div className="grid grid-cols-3 gap-3">
        <Field label="Beds"><input type="number" step="0.5" value={f.beds} onChange={e => setF({ ...f, beds: e.target.value })} className="input" data-testid="qa-beds" /></Field>
        <Field label="Baths"><input type="number" step="0.5" value={f.baths} onChange={e => setF({ ...f, baths: e.target.value })} className="input" data-testid="qa-baths" /></Field>
        <Field label="Sqft"><input type="number" value={f.sqft} onChange={e => setF({ ...f, sqft: e.target.value })} className="input" data-testid="qa-sqft" /></Field>
      </div>
      <Field label="Listing URL (MLS / Zillow)">
        <div className="relative">
          <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <input type="url" value={f.listing_url} onChange={e => setF({ ...f, listing_url: e.target.value })} placeholder="https://..." className="input pl-8" data-testid="qa-url" />
        </div>
      </Field>
      <Field label="Hero photo">
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={e => uploadPhoto(e.target.files?.[0])} />
        {f.hero_image_url ? (
          <div className="flex items-center gap-3">
            <img src={f.hero_image_url} alt="hero" className="h-16 w-24 rounded-md object-cover border border-slate-200" />
            <button type="button" onClick={pickPhoto} className="text-sm font-medium text-slate-600 hover:text-slate-900">Replace</button>
            <button type="button" onClick={() => setF({ ...f, hero_image_url: "" })} className="text-sm text-slate-400 hover:text-red-700">Remove</button>
          </div>
        ) : (
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <button type="button" onClick={pickPhoto} disabled={uploading} className="inline-flex items-center justify-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50" data-testid="qa-upload-btn">
              {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} {uploading ? "Uploading…" : "Upload photo"}
            </button>
            <span className="text-xs text-slate-500 sm:ml-1">or paste a photo URL below</span>
            <input type="url" value={f.hero_image_url} onChange={e => setF({ ...f, hero_image_url: e.target.value })} placeholder="https://..." className="input flex-1" data-testid="qa-photo-url" />
          </div>
        )}
      </Field>

      <div className="flex items-center justify-end gap-2 pt-2">
        <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 rounded-md px-5 py-2 text-sm font-semibold text-white disabled:opacity-50" style={{ background: "var(--primary-hex)" }} data-testid="qa-save-btn">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} {existing ? "Save changes" : "Save property"}
        </button>
      </div>
    </div>
  );
}

function ParseForm({ onSaved }) {
  const [text, setText] = useState("");
  const [parsed, setParsed] = useState(null);
  const [busy, setBusy] = useState(false);

  const parse = async () => {
    if (text.trim().length < 20) return toast.error("Paste a few lines from the listing");
    setBusy(true);
    try {
      const r = await api.post("/properties/parse", { text });
      setParsed({ ...r.data.suggestion, listing_url: "", hero_image_url: "" });
      toast.success(`Parsed with ${r.data.provider}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Parse failed");
    } finally { setBusy(false); }
  };

  const save = async () => {
    if (!parsed?.address_line1) return toast.error("Address is required — edit before saving");
    setBusy(true);
    try {
      const body = { ...parsed, entry_method: "parsed" };
      Object.keys(body).forEach(k => { if (body[k] === "" || body[k] === null) delete body[k]; });
      await api.post("/properties", body);
      toast.success("Property added");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  if (parsed) {
    return (
      <div className="p-6 space-y-3">
        <div className="flex items-center gap-2 rounded-md bg-amber-50 border border-amber-200 text-amber-900 px-3 py-2 text-xs">
          <Sparkles className="h-3.5 w-3.5" /> Review each field before saving. Never trust extractor output blind.
        </div>
        <Field label="Address"><input value={parsed.address_line1 || ""} onChange={e => setParsed({ ...parsed, address_line1: e.target.value })} className="input" data-testid="parse-address" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="City"><input value={parsed.city || ""} onChange={e => setParsed({ ...parsed, city: e.target.value })} className="input" /></Field>
          <Field label="State"><input value={parsed.state || ""} onChange={e => setParsed({ ...parsed, state: e.target.value.toUpperCase().slice(0,2) })} maxLength={2} className="input uppercase" /></Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Price"><input type="number" value={parsed.list_price ?? ""} onChange={e => setParsed({ ...parsed, list_price: e.target.value ? Number(e.target.value) : null })} className="input" /></Field>
          <Field label="Sqft"><input type="number" value={parsed.sqft ?? ""} onChange={e => setParsed({ ...parsed, sqft: e.target.value ? Number(e.target.value) : null })} className="input" /></Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Beds"><input type="number" step="0.5" value={parsed.beds ?? ""} onChange={e => setParsed({ ...parsed, beds: e.target.value ? Number(e.target.value) : null })} className="input" /></Field>
          <Field label="Baths"><input type="number" step="0.5" value={parsed.baths ?? ""} onChange={e => setParsed({ ...parsed, baths: e.target.value ? Number(e.target.value) : null })} className="input" /></Field>
        </div>
        <Field label="Description"><textarea value={parsed.description || ""} onChange={e => setParsed({ ...parsed, description: e.target.value })} className="input min-h-[80px]" /></Field>
        <div className="flex items-center justify-between gap-2 pt-2">
          <button onClick={() => setParsed(null)} className="text-sm text-slate-500 hover:text-slate-900">← Parse different text</button>
          <button onClick={save} disabled={busy} className="inline-flex items-center gap-1.5 rounded-md px-5 py-2 text-sm font-semibold text-white disabled:opacity-50" style={{ background: "var(--primary-hex)" }} data-testid="parse-save-btn">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Save reviewed property
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      <p className="text-sm text-slate-600">Paste the listing text from Zillow, Redfin, MLS remarks — Gemini extracts the fields for your review.</p>
      <textarea
        value={text}
        onChange={e => setText(e.target.value)}
        placeholder="Paste the listing description, remarks, and details here…"
        className="input min-h-[180px] w-full font-mono text-xs"
        data-testid="parse-textarea"
      />
      <div className="flex items-center justify-end">
        <button onClick={parse} disabled={busy || text.length < 20} className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 hover:bg-slate-800 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50" data-testid="parse-btn">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} Extract fields
        </button>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium uppercase tracking-widest text-slate-500 mb-1.5">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </span>
      {children}
    </label>
  );
}
